import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, CornerDownLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

type SearchResult = {
  id: string;
  label: string;
  section: string;
  method: string | null;
};

type SearchableSection = {
  title: string;
  links: { id: string; label: string; method: string | null }[];
};

export function DocsSearch({ sections }: { sections: SearchableSection[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const allResults: SearchResult[] = sections.flatMap((section) =>
    section.links.map((link) => ({
      id: link.id,
      label: link.label,
      section: section.title,
      method: link.method,
    })),
  );

  const filtered = useCallback(() => {
    if (!query.trim()) return allResults;
    const q = query.toLowerCase();
    return allResults.filter(
      (r) =>
        r.label.toLowerCase().includes(q) ||
        r.section.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q),
    );
  }, [query]);

  useEffect(() => {
    setResults(filtered());
    setActiveIdx(0);
  }, [filtered]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[activeIdx]) {
      e.preventDefault();
      window.location.hash = results[activeIdx].id;
      setOpen(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-500 hover:border-zinc-300 transition-all"
      >
        <Search className="size-4" strokeWidth={1.8} />
        <span className="hidden sm:inline">Search docs...</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-zinc-300 text-[10px] font-mono text-zinc-400">
          ⌘K
        </kbd>
      </button>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <div className="fixed left-1/2 top-[20%] -translate-x-1/2 z-[101] w-full max-w-xl px-4">
        <div className="bg-[var(--warm-surface)] rounded-[22px] shadow-2xl border border-zinc-200 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-200 ">
            <Search className="size-4 text-zinc-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search endpoints, sections..."
              className="flex-1 bg-transparent text-sm text-zinc-900 placeholder:text-zinc-400 outline-none"
              autoFocus
            />
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="max-h-[400px] overflow-y-auto p-2">
            {results.length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-8">
                No results found
              </p>
            ) : (
              results.map((result, i) => (
                <button
                  key={result.id}
                  onClick={() => {
                    window.location.hash = result.id;
                    setOpen(false);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all',
                    i === activeIdx
                      ? 'bg-[var(--warm-card)]'
                      : 'hover:bg-zinc-50 ',
                  )}
                >
                  {result.method && (
                    <span
                      className={cn(
                        'inline-flex items-center justify-center w-10 h-5 rounded text-[10px] font-bold font-mono shrink-0',
                        result.method === 'GET' && 'bg-green-100 text-green-700 ',
                        result.method === 'POST' && 'bg-blue-100 text-blue-700 ',
                        result.method === 'PATCH' && 'bg-amber-100 text-amber-700 ',
                        result.method === 'DELETE' && 'bg-red-100 text-red-700 ',
                      )}
                    >
                      {result.method}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">
                      {result.label}
                    </p>
                    <p className="text-xs text-zinc-400 truncate">{result.section}</p>
                  </div>
                  {i === activeIdx && (
                    <CornerDownLeft className="size-3.5 text-zinc-400 shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
          <div className="flex items-center gap-4 px-4 py-2.5 border-t border-zinc-200 text-[10px] text-zinc-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded border border-zinc-300 font-mono">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded border border-zinc-300 font-mono">↵</kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded border border-zinc-300 font-mono">esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
